import React, { useState, useEffect } from 'react';
import { Phone, MapPin, Menu, X, Globe, Calculator } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADVISOR_PHONE_DISPLAY, ADVISOR_PHONE } from './SEOHead';
import { trackWhatsAppClick, trackPhoneClick } from '../services/analytics';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'te' : 'en';
    i18n.changeLanguage(newLang);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = newLang;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const NavLink = ({ to, children }) => {
    const isActive = location.pathname === to;
    return (
      <Link 
        to={to} 
        className={`relative font-medium transition-all duration-300 hover:text-primary ${isActive ? 'text-primary' : 'text-slate-700 dark:text-slate-300'}`}
        onClick={() => setIsMenuOpen(false)}
      >
        {children}
        {isActive && (
          <span className="absolute -bottom-1 left-0 w-full h-[2px] bg-primary rounded-full" />
        )}
      </Link>
    );
  };

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'glass py-2 shadow-sm' : 'bg-transparent py-3'}`}>
      {/* Top Bar */}
      <div className={`overflow-hidden transition-all duration-300 ${scrolled ? 'h-0 opacity-0' : 'h-7 opacity-100 mb-2'}`}>
        <div className="container mx-auto px-6 h-full flex justify-between items-center text-xs font-medium text-slate-500 dark:text-slate-400 border-b border-border/40 pb-2">
          <div className="flex items-center gap-6">
            <a 
              href={`tel:${ADVISOR_PHONE}`} 
              onClick={() => trackPhoneClick('top_bar')}
              className="flex items-center gap-1.5 hover:text-primary transition-colors cursor-pointer"
            >
              <Phone className="w-3.5 h-3.5 text-primary" />
              <span>{ADVISOR_PHONE_DISPLAY}</span>
            </a>
            <span className="hidden md:flex items-center gap-1.5 hover:text-primary transition-colors">
              <MapPin className="w-3.5 h-3.5 text-rose-500" />
              {t('pan_india_service', 'Hyderabad & PAN-India Advisory')}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-emerald-600 dark:text-emerald-400 font-semibold">{t('years_experience', '25+ Years Experience')}</span>
            <span>{t('trusted_since_2000', 'Trusted since 2000')}</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container mx-auto px-6">
        <div className="flex justify-between items-center bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-2xl px-5 py-2.5 border border-white/60 dark:border-white/10 shadow-sm">
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/logo.png" 
              alt="VSR Insurance Plus Logo - LIC & Health Advisor V. S. Reddy" 
              width="40" 
              height="40" 
              className="w-10 h-10 object-contain drop-shadow-sm group-hover:scale-105 transition-transform duration-300" 
            />
            <div className="flex flex-col">
              <span className="text-base font-extrabold text-slate-900 dark:text-white uppercase tracking-wider leading-none">
                VSR Insurance Plus
              </span>
              <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium tracking-tight">
                {t('advisor_sub', 'V. S. Reddy • Senior Insurance Advisor')}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-5 text-sm" aria-label="Main Navigation">
            <NavLink to="/">{t('home')}</NavLink>
            <NavLink to="/lic-plans">{t('lic_plans')}</NavLink>
            <NavLink to="/child-insurance-plans">{t('child_plans')}</NavLink>
            <NavLink to="/pension-retirement-plans">{t('pension_plans')}</NavLink>
            <NavLink to="/health-insurance">{t('health_plans')}</NavLink>
            <NavLink to="/nri-insurance">{t('nri_insurance')}</NavLink>
            <NavLink to="/blog">{t('insurance_guides')}</NavLink>
            <NavLink to="/about">{t('about')}</NavLink>
            <NavLink to="/contact">{t('contact')}</NavLink>

            <Link 
              to="/nri-calculator" 
              className="flex items-center gap-1 text-orange-600 font-bold hover:text-orange-700 transition-colors text-xs bg-orange-50 dark:bg-orange-950/40 px-3 py-1.5 rounded-full border border-orange-200 dark:border-orange-800"
            >
              <Calculator className="w-3.5 h-3.5" /> {t('nri_calculator')}
            </Link>

            <div className="w-[1px] h-6 bg-slate-200 dark:bg-slate-700 mx-0.5"></div>

            <button
              onClick={toggleLanguage}
              aria-label={`Switch language to ${i18n.language === 'en' ? 'Telugu' : 'English'}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-700 dark:text-slate-300 text-xs font-semibold"
            >
              <Globe className="w-3.5 h-3.5 text-primary" />
              {i18n.language === 'en' ? 'తెలుగు' : 'English'}
            </button>

            <a
              href={`https://wa.me/919849664800?text=${encodeURIComponent(i18n.language === 'te' ? 'నమస్కారం వి. ఎస్. రెడ్డి గారు, నేను పాలసీ కొటేషన్ మరియు ఉచిత సలహా కొరకు సంప్రదిస్తున్నాను.' : 'Hello V. S. Reddy, I would like to get a quote and consultation for insurance planning.')}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('header_get_a_quote')}
              className="bg-primary text-white text-xs font-bold px-4 py-2 rounded-full shadow-md shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all duration-300 flex items-center gap-1.5"
            >
              <Phone className="w-3.5 h-3.5" /> {t('get_a_quote')}
            </a>
          </nav>

          {/* Medium Screens (lg-xl) condensed nav */}
          <div className="hidden lg:flex xl:hidden items-center gap-3">
            <NavLink to="/">{t('home')}</NavLink>
            <NavLink to="/lic-plans">{t('lic_plans')}</NavLink>
            <NavLink to="/health-insurance">{t('health_plans')}</NavLink>
            <NavLink to="/blog">{t('insurance_guides')}</NavLink>
            <NavLink to="/about">{t('about')}</NavLink>
            <NavLink to="/contact">{t('contact')}</NavLink>
            <button
              onClick={toggleLanguage}
              aria-label="Switch Language"
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-800"
            >
              <Globe className="w-3.5 h-3.5 text-primary" />
              {i18n.language === 'en' ? 'తెలుగు' : 'English'}
            </button>
            <a
              href="https://wa.me/919849664800"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary text-white text-xs font-bold px-3 py-1.5 rounded-full"
            >
              {t('quote')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-slate-800 dark:text-white p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation Dropdown */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full px-4 pt-2">
            <nav className="glass-card bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl rounded-3xl p-6 flex flex-col gap-3 shadow-2xl border border-slate-200 dark:border-slate-800 text-sm max-h-[80vh] overflow-y-auto">
              <NavLink to="/">{t('home')}</NavLink>
              <NavLink to="/lic-plans">{t('lic_plans')}</NavLink>
              <NavLink to="/child-insurance-plans">{t('child_plans')}</NavLink>
              <NavLink to="/pension-retirement-plans">{t('pension_plans')}</NavLink>
              <NavLink to="/health-insurance">{t('health_plans')}</NavLink>
              <NavLink to="/nri-insurance">{t('nri_insurance')}</NavLink>
              <NavLink to="/blog">{t('insurance_guides')}</NavLink>
              <NavLink to="/about">{t('about')}</NavLink>
              <NavLink to="/contact">{t('contact')}</NavLink>

              <NavLink to="/nri-calculator">
                <span className="text-orange-600 font-bold flex items-center gap-2">
                  <Calculator className="w-4 h-4" /> {t('nri_calculator')}
                </span>
              </NavLink>

              <div className="h-[1px] bg-slate-200 dark:bg-slate-800 w-full my-2"></div>

              <div className="flex justify-between items-center gap-3">
                <button
                  onClick={toggleLanguage}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-xl text-xs font-semibold"
                >
                  <Globe className="w-4 h-4 text-primary" />
                  {i18n.language === 'en' ? 'తెలుగులోకి మార్చండి' : 'Switch to English'}
                </button>
                <a
                  href={`https://wa.me/919849664800?text=${encodeURIComponent(i18n.language === 'te' ? 'నమస్కారం వి. ఎస్. రెడ్డి గారు, నేను పాలసీ కొటేషన్ కోసం సంప్రదిస్తున్నాను.' : 'Hi V. S. Reddy, I want to get an insurance quote.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-primary text-white text-xs font-bold px-5 py-2.5 rounded-xl shadow-md flex items-center gap-1.5"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Phone className="w-3.5 h-3.5" /> {t('whatsapp_expert')}
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
